import axios from "axios";
import { useFormik } from "formik";
import { enqueueSnackbar } from "notistack";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import * as yup from "yup";
import { API, DiscountTypes } from "../../../helpers/constants";
import { handleAxiosErrors, logoutUnauthorizedUser } from "../../../helpers/errorHandling";
import {
	calcTotalWithDiscount,
	calcTotalWithMarkup,
	isUUID
} from "../../../helpers/helpers";
import useUserAction from "../../../hooks/user/useUserAction";
import useUserData from "../../../hooks/user/useUserData";
import { ISSUES_FORM } from "../locales/namespaces";

// helper: normalize numeric inputs from NumericFormat/TextField ("", "0", "10")
const toNumberOrNull = (v) => (v === "" || v === undefined || v === null ? null : Number(v));

const useCustomerSOForm = ({
							   issueId, data, onSubmitSuccess = () => {
	}, onSubmitFailure = () => {
	}
						   }) => {
	const { t } = useTranslation(ISSUES_FORM)
	const { token, username, userId, isLoggedIn, dateFormat, timeFormat } = useUserData()
	const { userLogout } = useUserAction()
	const [loadingSubmit, setLoadingSubmit] = useState(false)
	const onSubmit = (values) => {
		setLoadingSubmit(true)
		axios({
			method: 'post',
			headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
			data: {
				...(values.soId && { id: values.soId }),
				IssueId: issueId,
				MarkUp: parseFloat(values.markup),
				isPCT: values.discountType,
				Discount: values.discount,
				Comments: values.comment,
				items: values.items.map((item) => ({
					...(!isUUID(item.id) && { id: Number(item.id) }),
					ItemId: item.ItemId?.id ? item.ItemId?.id : '',
					ItemDescription: item.ItemDescription,
					Qty: item.Qty ? parseFloat(item.Qty) : 0,
					ItemValue: item.ItemPrice ? parseFloat(item.ItemPrice) : 0,
					ChargeDescription: item.ChargeDescription,
					PCTCharge: item.PCTCharge ? parseFloat(item.PCTCharge) : 0,
					Note: item.Note,
				})),
				charges: values.charges.map(charge => ({
					...(!isUUID(charge.id) && { id: Number(charge.id) }),
					ChargeDescription: charge.ChargeDescription,
					PCTCharge: charge.PCTCharge,
					Note: charge.Note,
				}))
			},
			url: values.soId ? `${API}/en/custom/workOrder/update` : `${API}/en/custom/workOrder/store`
		}).then(response => {
			enqueueSnackbar(t(`Customer offer submitted successfully!`), {
				variant: 'success',
				autoHideDuration: 3000,
			})
			setLoadingSubmit(false)
			onSubmitSuccess({ values, issueId, data, response })
		}).catch((error) => {
			setLoadingSubmit(false)
			const errorMessage = `${t('An error occurred!')} ${error?.response?.data?.errors}`;
			enqueueSnackbar(handleAxiosErrors(error, errorMessage), {
				variant: 'error',
				autoHideDuration: 3000,
			})
			onSubmitFailure({ values, issueId, data, error })
			logoutUnauthorizedUser(error, isLoggedIn, userLogout)
		})
	}
	
	const validationSchema = yup.object({
		items: yup.array()
		.of(
			yup
			.object({
				id: yup.string().required(),
				ItemId: yup.mixed().nullable(),
				ItemDescription: yup.string().nullable(),
				// Qty: required & > 0 when item exists
				Qty: yup.number()
				.required(t('Required'))
				.moreThan(0, t('Required')),
				ItemPrice: yup.number()
				.required(t('Required'))
				.moreThan(0, t('Required'))
			})
			// 1) ItemId OR ItemDescription required → attach to Description
			.test("itemid-or-description", function (item) {
				if (!item) return false;
				if ((item.ItemId && item.ItemId.id) || item.ItemDescription) return true;
				return this.createError({
					path: `${this.path}.ItemDescription`,
					message: t("Either Item or Description is required"),
				});
			})
			// 3) Pair rule (no cycle): both surcharge fields together or both empty
			.test("surcharge-pair", function (item) {
				if (!item) return true;
				const hasPct = item.PCTCharge !== null && item.PCTCharge !== undefined && item.PCTCharge !== 0;
				const hasDesc = !!item.ChargeDescription;
				if (hasPct !== hasDesc) {
					return this.createError({
						path: `${this.path}.ChargeDescription`,
						message: t("Charge description and percentage must both be filled or both empty"),
					});
				}
				return true;
			})
		)
		// Rule 4: At least one item required → attach to allItemsError (NOT items)
		.test("at-least-one-item", function (arr) {
			if (Array.isArray(arr) && arr.length > 0) return true;
			return this.createError({
				path: "items.allItemsError",
				message: t("At least one item is required"),
			});
		}),
		charges: yup.array().of(
			yup
			.object({
				id: yup.string().required(),
				ChargeDescription: yup.string().required(t('Required')),
				PCTCharge: yup
				.number()
				.transform((v, orig) => toNumberOrNull(orig))
				.moreThan(0, t('Required'))
				.required(t('Required')),
				Note: yup.string().nullable(),
			})
		),
		discount: yup.mixed()
		.test('discount-cannot-be-more-than-total', t("Calculated discount can not be more that total"), function (value) {
			const {charges, items, markup, discountType} = this.parent
			const total = calcTotalWithMarkup(items, charges, markup)
			const totalWithDiscount = calcTotalWithDiscount(items, charges, markup, {discount: value, discountType: discountType})
			console.log(totalWithDiscount, total, totalWithDiscount <= total)
			return totalWithDiscount >= 0 && totalWithDiscount <= total;
		})
	});
	
	const formik = useFormik({
		initialValues: {
			items: data?.items || [],
			charges: data?.charges || [],
			comment: data?.comment || '',
			soId: data?.soId || null,
			markup: data?.markup || 0,
			discount: data?.discount || 0,
			discountType: data?.discountType ? Number(data.discountType) : DiscountTypes.Percentage,
		},
		validationSchema: validationSchema,
		onSubmit: onSubmit,
	})
	
	return {
		formik,
		loadingSubmit
	}
}

export default useCustomerSOForm